<script setup>
import { computed, ref, onMounted, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import AiCloserAPI from 'dashboard/api/aiCloser';
import { usePolicy } from 'dashboard/composables/usePolicy';
import PageLayout from 'dashboard/components-next/ailab/PageLayout.vue';
import DeleteDialog from 'dashboard/components-next/ailab/pageComponents/DeleteDialog.vue';
import AilabInboxesAPI from 'dashboard/api/ailab/inboxes';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { checkPermissions } = usePolicy();
const { accountId } = useAccount();
const alert = useAlert;

const searchQuery = ref('');
const viewMode = ref('grid');
const selectedAssistant = ref(null);
const deleteAssistantDialog = ref(null);
const canDelete = computed(() => checkPermissions(['administrator']));
const isCreating = ref(false);
const error = ref('');

// Dialogs (Inboxes + Reply Mode)
const inboxesDialogRef = ref(null);
const modeDialogRef = ref(null);
const inboxesAssistant = ref(null);
const modeAssistant = ref(null);
const inboxesLoading = ref(false);
const modalInboxes = ref([]);
const modeValue = ref('auto_reply');
const modeSaving = ref(false);
const modeAssistantId = ref(null);

// Store config status for each assistant (keyed by assistant id)
const configStatuses = reactive({});

const uiFlags = useMapGetter('ailabAssistants/getUIFlags');
const assistants = useMapGetter('ailabAssistants/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);

const categoryAssistants = computed(() => {
  const list = assistants.value || [];
  // Default category is AI Closer when wlab_sidebar_key is missing.
  return list.filter(
    a => (a?.config?.wlab_sidebar_key || 'ai_closer') === 'ai_closer'
  );
});

const filteredAssistants = computed(() => {
  let list = categoryAssistants.value || [];
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    list = list.filter(
      a =>
        a.name?.toLowerCase().includes(query) ||
        a.description?.toLowerCase().includes(query)
    );
  }
  return list;
});

// Fetch AI Closer config status for each assistant
const fetchConfigStatuses = async () => {
  const list = categoryAssistants.value || [];
  for (const assistant of list) {
    try {
      const response = await AiCloserAPI.getConfig(assistant.id);
      configStatuses[assistant.id] = response.data?.status || 'inactive';
    } catch {
      // If no config exists, default to inactive
      configStatuses[assistant.id] = 'inactive';
    }
  }
};

// Get status for a specific assistant
const getAssistantStatus = assistantId => {
  return configStatuses[assistantId] || 'inactive';
};

// Check if assistant is active
const isAssistantActive = assistantId => {
  return getAssistantStatus(assistantId) === 'active';
};

const replyModeLabel = assistant => {
  const mode = (
    assistant?.config?.wlab_ai_reply_mode || 'auto_reply'
  ).toString();
  if (mode === 'auto_suggest')
    return t('AILAB.AI_CLOSER_PAGE.REPLY_MODE.AUTO_SUGGEST');
  if (mode === 'suggest') return t('AILAB.AI_CLOSER_PAGE.REPLY_MODE.SUGGEST');
  return t('AILAB.AI_CLOSER_PAGE.REPLY_MODE.AUTO_REPLY');
};

const replyModeClass = assistant => {
  const mode = (
    assistant?.config?.wlab_ai_reply_mode || 'auto_reply'
  ).toString();
  if (mode === 'auto_suggest') return 'bg-cyan-500/20 text-cyan-400';
  if (mode === 'suggest') return 'bg-purple-500/20 text-purple-400';
  return 'bg-blue-500/20 text-blue-400';
};

const getAssistantCardBorderStyle = assistant => {
  const active = isAssistantActive(assistant?.id);
  const mode = (
    assistant?.config?.wlab_ai_reply_mode || 'auto_reply'
  ).toString();
  let borderColor = '#94a3b8';
  if (active) {
    if (mode === 'auto_suggest') borderColor = '#06b6d4';
    else if (mode === 'suggest') borderColor = '#a855f7';
    else borderColor = '#3b82f6';
  }
  return { borderLeft: `4px solid ${borderColor}` };
};

const toggleAssistantStatus = async assistant => {
  const id = assistant?.id;
  if (!id) return;
  const next = isAssistantActive(id) ? 'inactive' : 'active';
  try {
    await AiCloserAPI.updateConfig(id, { status: next });
    configStatuses[id] = next;
    alert(next === 'active' ? '✅ Activated' : '⏸️ Set to inactive');
  } catch (e) {
    alert('❌ Failed to update status');
  }
};

const openInboxesModal = async assistant => {
  inboxesAssistant.value = assistant;
  inboxesDialogRef.value?.open?.();
  inboxesLoading.value = true;
  modalInboxes.value = [];
  try {
    const resp = await AilabInboxesAPI.get({ assistantId: assistant.id });
    modalInboxes.value = resp?.data?.payload || [];
  } catch (e) {
    alert(e?.response?.data?.error || e?.message || 'Failed to load inboxes');
  } finally {
    inboxesLoading.value = false;
  }
};

const closeInboxesModal = () => {
  inboxesDialogRef.value?.close?.();
};

const onInboxesModalClose = () => {
  inboxesAssistant.value = null;
  modalInboxes.value = [];
};

const openModeModal = assistant => {
  modeAssistant.value = assistant;
  modeAssistantId.value = assistant?.id || null;
  modeValue.value = (
    assistant?.config?.wlab_ai_reply_mode || 'auto_reply'
  ).toString();
  modeDialogRef.value?.open?.();
};

const closeModeModal = () => {
  modeDialogRef.value?.close?.();
};

const onModeModalClose = () => {
  modeAssistant.value = null;
  modeAssistantId.value = null;
};

const saveMode = async () => {
  const assistantId = modeAssistantId.value || modeAssistant.value?.id;
  if (!assistantId) {
    alert('Failed to identify assistant. Please reopen this dialog.');
    return;
  }
  const currentAssistant =
    modeAssistant.value ||
    (assistants.value || []).find(a => a.id === assistantId);
  modeSaving.value = true;
  try {
    const nextCfg = {
      ...(currentAssistant?.config || {}),
      wlab_ai_reply_mode: modeValue.value,
    };
    await store.dispatch('ailabAssistants/update', {
      id: assistantId,
      assistant: { config: nextCfg },
    });
    await store.dispatch('ailabAssistants/get');
    alert('✅ Updated reply mode');
    closeModeModal();
  } catch (e) {
    alert(e?.message || 'Failed to update mode');
  } finally {
    modeSaving.value = false;
  }
};

const openAssistant = assistant => {
  router.push({
    name: 'ailab_ai_closer_assistant_v2',
    params: { accountId: accountId.value, assistantId: assistant.id },
  });
};

const nextAiCloserName = () => {
  const base = 'AI Closer';
  const existing = (categoryAssistants.value || [])
    .map(a => a?.name)
    .filter(Boolean);
  if (!existing.includes(base)) return base;
  let i = 2;
  while (existing.includes(`${base} ${String(i).padStart(2, '0')}`)) i += 1;
  return `${base} ${String(i).padStart(2, '0')}`;
};

const createAiCloser = async () => {
  isCreating.value = true;
  error.value = '';
  try {
    const created = await store.dispatch('ailabAssistants/create', {
      assistant: {
        name: nextAiCloserName(),
        description: 'AI Closer assistant',
        config: { wlab_sidebar_key: 'ai_closer' },
        response_guidelines: [],
        guardrails: [],
      },
    });
    const newAssistantId = created?.id;
    await store.dispatch('ailabAssistants/get');
    if (newAssistantId) {
      router.push({
        name: 'ailab_ai_closer_assistant_v2',
        params: { accountId: accountId.value, assistantId: newAssistantId },
      });
    }
  } catch (e) {
    error.value =
      e?.response?.data?.message ||
      e?.message ||
      'Failed to create AI Closer. Please try again.';
  } finally {
    isCreating.value = false;
  }
};

const handleDelete = assistant => {
  if (!canDelete.value) return;
  selectedAssistant.value = assistant;
  deleteAssistantDialog.value?.dialogRef?.open();
};

const onDeleteSuccess = async () => {
  selectedAssistant.value = null;
  await store.dispatch('ailabAssistants/get');
};

const parseMaybeUnixTimestamp = input => {
  if (input === null || input === undefined || input === '') return null;
  // Backend sometimes returns unix seconds (int) instead of ISO string.
  // If we feed seconds into new Date(), it becomes Jan 1970. Normalize.
  const s = typeof input === 'string' ? input.trim() : input;
  const n = typeof s === 'number' ? s : Number(s);
  if (Number.isFinite(n) && `${s}`.match(/^\d+$/)) {
    // Heuristic: < 1e12 => seconds, otherwise milliseconds
    const ms = n < 1e12 ? n * 1000 : n;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = raw => {
  const date = parseMaybeUnixTimestamp(raw);
  if (!date) return 'N/A';
  try {
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // If within 7 days, show relative time
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;

    // Otherwise show formatted date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  } catch (e) {
    return 'N/A';
  }
};

// Watch for assistants list changes and fetch configs
watch(
  () => assistants.value,
  newVal => {
    if (newVal && newVal.length > 0) {
      fetchConfigStatuses();
    }
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('ailabAssistants/get');
});
</script>

<template>
  <PageLayout
    :is-fetching="isFetching"
    :is-empty="!isFetching && !filteredAssistants.length"
    :show-pagination-footer="false"
    :button-label="t('AILAB.AI_CLOSER_PAGE.ACTIONS.NEW_WORKFLOW')"
    :is-button-loading="isCreating"
    :disable-button="isCreating"
    content-max-width-class="max-w-none"
    @click="createAiCloser"
  >
    <template #headerTitle>
      <div class="flex flex-col min-w-0">
        <h1 class="text-xl font-semibold text-slate-900 truncate">
          {{ t('AILAB.AI_CLOSER_PAGE.TITLE') }}
        </h1>
        <div class="text-sm text-slate-500">
          {{
            t('AILAB.AI_CLOSER_PAGE.RESULT_COUNT', {
              count: filteredAssistants.length,
            })
          }}
        </div>
      </div>
    </template>

    <template #knowMore>
      <Button
        variant="outline"
        color="slate"
        size="sm"
        icon="i-lucide-book-open"
        :label="t('AILAB.AI_CLOSER_PAGE.ACTIONS.LEARN_MORE')"
      />
    </template>

    <template #controls>
      <div class="flex flex-col gap-4">
        <div
          class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl border border-slate-200 bg-white"
        >
          <p class="text-sm text-slate-600 m-0">
            {{ t('AILAB.AI_CLOSER_PAGE.DESCRIPTION.MAIN') }}
          </p>
        </div>

        <div
          class="flex flex-col sm:flex-row sm:items-center justify-between gap-3"
        >
          <div class="relative w-full sm:w-96">
            <span
              class="absolute i-lucide-search size-4 top-[35%] -translate-y-1/2 left-3 text-slate-500 pointer-events-none"
            />
            <input
              v-model="searchQuery"
              type="search"
              class="w-full h-10 !pl-12 pr-3 text-sm outline-none border border-slate-200 rounded-lg bg-white text-slate-900 placeholder:text-slate-500 appearance-none"
              :placeholder="t('AILAB.AI_CLOSER_PAGE.SEARCH_PLACEHOLDER')"
            />
          </div>

          <Button
            variant="outline"
            color="slate"
            size="sm"
            :icon="
              viewMode === 'grid' ? 'i-lucide-list' : 'i-lucide-layout-grid'
            "
            :label="
              viewMode === 'grid'
                ? t('AILAB.AI_CLOSER_PAGE.VIEW.LIST')
                : t('AILAB.AI_CLOSER_PAGE.VIEW.GRID')
            "
            @click="viewMode = viewMode === 'grid' ? 'list' : 'grid'"
          />
        </div>

        <div
          v-if="error"
          class="flex items-center gap-2 px-4 py-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-700"
        >
          <span class="i-lucide-alert-circle size-4" />
          <span class="text-sm">{{ error }}</span>
        </div>
      </div>
    </template>

    <template #emptyState>
      <div
        class="flex flex-col items-center justify-center py-20 text-center px-6"
      >
        <div
          class="w-20 h-20 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-500"
        >
          <span class="i-lucide-sparkles size-10" />
        </div>
        <h3 class="mt-6 text-lg font-semibold text-slate-900">
          {{ t('AILAB.AI_CLOSER_PAGE.EMPTY.TITLE') }}
        </h3>
        <p class="mt-2 text-sm text-slate-600 max-w-xl">
          {{ t('AILAB.AI_CLOSER_PAGE.EMPTY.SUBTITLE') }}
        </p>
        <Button
          class="mt-6"
          icon="i-lucide-plus"
          size="sm"
          :label="t('AILAB.AI_CLOSER_PAGE.ACTIONS.CREATE_AI_CLOSER')"
          :is-loading="isCreating"
          :disabled="isCreating"
          @click="createAiCloser"
        />
      </div>
    </template>

    <template #body>
      <div
        v-if="viewMode === 'grid'"
        class="grid grid-cols-1 xl:grid-cols-2 gap-4"
      >
        <div
          v-for="assistant in filteredAssistants"
          :key="assistant.id"
          class="p-5 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors cursor-pointer"
          @click="openAssistant(assistant)"
        >
          <div class="flex items-start gap-4">
            <div
              class="w-11 h-11 rounded-xl bg-slate-50 text-slate-900 flex items-center justify-center flex-shrink-0 border border-slate-200"
            >
              <span class="i-lucide-sparkles size-5" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-start justify-between gap-3">
                <h3 class="text-base font-semibold text-slate-900 truncate">
                  {{ assistant.name }}
                </h3>
                <span
                  class="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium"
                  :class="
                    isAssistantActive(assistant.id)
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-slate-50 text-slate-600 border border-slate-200'
                  "
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full"
                    :class="
                      isAssistantActive(assistant.id)
                        ? 'bg-emerald-500'
                        : 'bg-slate-400'
                    "
                  />
                  {{
                    isAssistantActive(assistant.id)
                      ? t('AILAB.AI_CLOSER_PAGE.META.ACTIVE')
                      : t('AILAB.AI_CLOSER_PAGE.META.INACTIVE')
                  }}
                </span>
              </div>
              <p class="mt-1 text-sm text-slate-600 line-clamp-2">
                {{
                  assistant.description ||
                  t('AILAB.AI_CLOSER_PAGE.META.NO_DESCRIPTION')
                }}
              </p>
            </div>
          </div>

          <div class="mt-4 grid grid-cols-2 gap-3">
            <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <div class="text-xs text-slate-500">
                {{ t('AILAB.AI_CLOSER_PAGE.META.MODE') }}
              </div>
              <div class="mt-1 text-sm text-slate-900 truncate">
                {{ replyModeLabel(assistant) }}
              </div>
            </div>
            <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <div class="text-xs text-slate-500">
                {{ t('AILAB.AI_CLOSER_PAGE.META.UPDATED') }}
              </div>
              <div class="mt-1 text-sm text-slate-900 truncate">
                {{ formatDate(assistant.updated_at) }}
              </div>
            </div>
          </div>

          <div class="mt-4 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              color="slate"
              :icon="
                isAssistantActive(assistant.id)
                  ? 'i-lucide-pause'
                  : 'i-lucide-play'
              "
              :label="
                isAssistantActive(assistant.id)
                  ? t('AILAB.AI_CLOSER_PAGE.BUTTONS.PAUSE')
                  : t('AILAB.AI_CLOSER_PAGE.BUTTONS.ACTIVATE')
              "
              @click.stop="toggleAssistantStatus(assistant)"
            />
            <Button
              size="sm"
              variant="outline"
              color="slate"
              icon="i-lucide-inbox"
              :label="t('AILAB.AI_CLOSER_PAGE.BUTTONS.INBOXES')"
              @click.stop="openInboxesModal(assistant)"
            />
            <Button
              size="sm"
              variant="outline"
              color="slate"
              icon="i-lucide-sliders-horizontal"
              :label="t('AILAB.AI_CLOSER_PAGE.BUTTONS.MODE')"
              @click.stop="openModeModal(assistant)"
            />
            <Button
              v-if="canDelete"
              size="sm"
              variant="outline"
              color="ruby"
              icon="i-lucide-trash-2"
              :label="t('AILAB.AI_CLOSER_PAGE.BUTTONS.DELETE')"
              @click.stop="handleDelete(assistant)"
            />
          </div>
        </div>
      </div>

      <div v-else class="flex flex-col gap-2">
        <div
          v-for="assistant in filteredAssistants"
          :key="assistant.id"
          class="flex items-center gap-4 p-4 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors cursor-pointer"
          @click="openAssistant(assistant)"
        >
          <div
            class="w-10 h-10 rounded-xl bg-slate-50 text-slate-900 flex items-center justify-center flex-shrink-0 border border-slate-200"
          >
            <span class="i-lucide-sparkles size-5" />
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-3">
              <div class="text-sm font-semibold text-slate-900 truncate">
                {{ assistant.name }}
              </div>
              <span class="text-xs text-slate-500">
                {{ formatDate(assistant.updated_at) }}
              </span>
            </div>
            <div class="text-sm text-slate-600 truncate">
              {{
                assistant.description ||
                t('AILAB.AI_CLOSER_PAGE.META.NO_DESCRIPTION')
              }}
            </div>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="ghost"
              color="slate"
              :icon="
                isAssistantActive(assistant.id)
                  ? 'i-lucide-pause'
                  : 'i-lucide-play'
              "
              @click.stop="toggleAssistantStatus(assistant)"
            />
            <Button
              size="sm"
              variant="ghost"
              color="slate"
              icon="i-lucide-inbox"
              @click.stop="openInboxesModal(assistant)"
            />
            <Button
              size="sm"
              variant="ghost"
              color="slate"
              icon="i-lucide-sliders-horizontal"
              @click.stop="openModeModal(assistant)"
            />
            <Button
              v-if="canDelete"
              size="sm"
              variant="ghost"
              color="ruby"
              icon="i-lucide-trash-2"
              @click.stop="handleDelete(assistant)"
            />
          </div>
        </div>
      </div>
    </template>
  </PageLayout>

  <DeleteDialog
    v-if="selectedAssistant"
    ref="deleteAssistantDialog"
    :entity="selectedAssistant"
    type="Assistants"
    @delete-success="onDeleteSuccess"
  />

  <Dialog
    ref="inboxesDialogRef"
    :title="t('AILAB.AI_CLOSER_PAGE.MODAL.INBOXES_TITLE')"
    :show-confirm-button="false"
    :is-loading="inboxesLoading"
    width="xl"
    @close="onInboxesModalClose"
  >
    <div class="text-sm text-n-slate-11">
      {{ inboxesAssistant?.name }}
    </div>
    <div v-if="inboxesLoading" class="text-sm text-n-slate-11">
      {{ t('AILAB.AI_CLOSER_PAGE.MODAL.LOADING') }}
    </div>
    <div v-else-if="!modalInboxes.length" class="text-sm text-n-slate-11">
      {{ t('AILAB.AI_CLOSER_PAGE.MODAL.NO_INBOX') }}
    </div>
    <div v-else class="flex flex-col gap-2">
      <div
        v-for="inbox in modalInboxes"
        :key="inbox.id"
        class="p-3 rounded-xl border border-n-weak bg-n-solid-2"
      >
        <div class="text-sm font-medium text-n-slate-12">
          {{ inbox.name }}
        </div>
        <div class="text-xs text-n-slate-11">
          {{ inbox.channel_type || t('AILAB.AI_CLOSER_PAGE.MODAL.CHANNEL') }}
        </div>
      </div>
    </div>
  </Dialog>

  <Dialog
    ref="modeDialogRef"
    :title="t('AILAB.AI_CLOSER_PAGE.MODAL.REPLY_MODE_TITLE')"
    :confirm-button-label="t('AILAB.AI_CLOSER_PAGE.MODAL.SAVE')"
    :cancel-button-label="t('AILAB.AI_CLOSER_PAGE.MODAL.CANCEL')"
    :disable-confirm-button="modeSaving"
    :is-loading="modeSaving"
    width="lg"
    @confirm="saveMode"
    @close="onModeModalClose"
  >
    <div class="text-sm text-n-slate-11">
      {{ modeAssistant?.name }}
    </div>
    <select
      v-model="modeValue"
      class="w-full h-10 px-3 text-sm outline-none border border-n-weak rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12"
    >
      <option value="auto_reply">
        {{ t('AILAB.AI_CLOSER_PAGE.MODAL.OPTION_AUTO_REPLY') }}
      </option>
      <option value="suggest">
        {{ t('AILAB.AI_CLOSER_PAGE.MODAL.OPTION_SUGGEST') }}
      </option>
      <option value="auto_suggest">
        {{ t('AILAB.AI_CLOSER_PAGE.MODAL.OPTION_AUTO_SUGGEST') }}
      </option>
    </select>
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="t('AILAB.AI_CLOSER_PAGE.MODAL.CANCEL')"
          class="w-full"
          type="button"
          @click="closeModeModal"
        />
        <Button
          :label="t('AILAB.AI_CLOSER_PAGE.MODAL.SAVE')"
          class="w-full"
          :is-loading="modeSaving"
          :disabled="modeSaving"
          type="button"
          @click="saveMode"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
/* Main Container */
.ai-closer-page {
  --ailab-bg-page: #1f1f1f;
  --ailab-bg-surface: #2b2d31;
  --ailab-bg-surface-hover: #2f3136;
  --ailab-bg-subtle: #1f1f1f;
  --ailab-bg-content: #12161c;
  --ailab-text-primary: #e5e7eb;
  --ailab-text-secondary: #a8aaae;
  --ailab-text-muted: #6b6f76;
  --ailab-accent: #5b5fc7;
  --ailab-accent-hover: #6b6fd6;
  --ailab-accent-soft: #8b8fd8;
  --ailab-danger: #f38ba8;
  --ailab-danger-soft: #fca5a5;
  --ailab-border: rgba(255, 255, 255, 0.12);
  --ailab-border-strong: rgba(255, 255, 255, 0.22);
  --ailab-divider: rgba(255, 255, 255, 0.08);
  --ailab-overlay-soft: rgba(255, 255, 255, 0.05);
  background: var(--ailab-bg-page);
  color: var(--ailab-text-primary);
}

:global(body:not(.dark) .ai-closer-page) {
  --ailab-bg-page: #f5f7fb;
  --ailab-bg-surface: #ffffff;
  --ailab-bg-surface-hover: #f8fafc;
  --ailab-bg-subtle: #f1f5f9;
  --ailab-bg-content: #eef2f7;
  --ailab-text-primary: #111827;
  --ailab-text-secondary: #4b5563;
  --ailab-text-muted: #6b7280;
  --ailab-accent: #4f46e5;
  --ailab-accent-hover: #4338ca;
  --ailab-accent-soft: #4f46e5;
  --ailab-danger: #dc2626;
  --ailab-danger-soft: #dc2626;
  --ailab-border: rgba(15, 23, 42, 0.14);
  --ailab-border-strong: rgba(15, 23, 42, 0.22);
  --ailab-divider: rgba(15, 23, 42, 0.1);
  --ailab-overlay-soft: rgba(15, 23, 42, 0.04);
}

/* Header */
.ai-closer-header {
  background: var(--ailab-bg-surface);
  border-bottom: 1px solid var(--ailab-divider);
}

.primary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  background: var(--ailab-accent);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.primary-btn:hover:not(:disabled) {
  background: var(--ailab-accent-hover);
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.secondary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  background: transparent;
  color: var(--ailab-text-primary);
  border: 1px solid var(--ailab-border-strong);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.secondary-btn:hover {
  background: var(--ailab-overlay-soft);
  border-color: var(--ailab-border-strong);
}

/* Description Banner */
.description-banner {
  background: var(--ailab-bg-surface);
  border-bottom: 1px solid var(--ailab-divider);
  padding: 20px 32px;
}

.description-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.description-text {
  flex: 1;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ailab-text-secondary);
}

.learn-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  background: transparent;
  color: var(--ailab-text-primary);
  border: 1px solid var(--ailab-border-strong);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.learn-more-btn:hover {
  background: var(--ailab-overlay-soft);
}

/* Error Banner */
.error-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 32px;
  background: rgba(239, 68, 68, 0.1);
  border-bottom: 1px solid rgba(239, 68, 68, 0.2);
  color: var(--ailab-danger-soft);
  font-size: 14px;
}

/* Inline Search (in Header) */
.search-container-inline {
  position: relative;
  width: 240px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: var(--ailab-text-muted);
}

.search-input-inline {
  width: 100%;
  padding: 8px 12px;
  padding-left: 35px;
  background: var(--ailab-overlay-soft);
  border: 1px solid var(--ailab-border);
  border-radius: 6px;
  color: var(--ailab-text-primary);
  font-size: 13px;
  transition: all 0.2s ease;
}

.search-input-inline:focus {
  outline: none;
  border-color: var(--ailab-border-strong);
  background: var(--ailab-overlay-soft);
}

.search-input-inline::placeholder {
  color: var(--ailab-text-muted);
}

.results-count-inline {
  font-size: 13px;
  color: var(--ailab-text-secondary);
  white-space: nowrap;
}

/* Content Area */
.content-area {
  background: var(--ailab-bg-content);
}

:global(body:not(.dark) .content-area) {
  background: var(--wlab-bg-primary);
}

/* Loading */
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--ailab-border);
  border-top-color: var(--ailab-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 16px;
  background: var(--ailab-bg-surface);
  border: 1px solid var(--ailab-border);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ailab-text-muted);
  margin-bottom: 20px;
}

.empty-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--ailab-text-primary);
  margin-bottom: 8px;
}

.empty-description {
  font-size: 14px;
  color: var(--ailab-text-secondary);
  margin-bottom: 24px;
}

/* Workflows Container */
.workflows-container {
  padding: 32px;
}

.workflows-header {
  margin-bottom: 20px;
}

.workflows-count {
  font-size: 16px;
  font-weight: 600;
  color: var(--ailab-text-primary);
}

/* Workflow Card (Grid View) */
.workflows-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 16px;
}

.workflow-card {
  background: var(--ailab-bg-surface);
  border: 1px solid var(--ailab-border);
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.workflow-card:hover {
  border-color: var(--ailab-border-strong);
  background: var(--ailab-bg-surface-hover);
}

.workflow-card-header {
  display: flex;
  gap: 14px;
  margin-bottom: 20px;
}

.workflow-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(91, 95, 199, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ailab-accent-soft);
  flex-shrink: 0;
}

.workflow-title-section {
  flex: 1;
  min-width: 0;
}

.workflow-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--ailab-text-primary);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.workflow-description {
  font-size: 13px;
  color: var(--ailab-text-secondary);
  line-height: 1.4;
}

.workflow-meta {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  padding: 16px;
  background: var(--ailab-bg-subtle);
  border-radius: 8px;
}

.workflow-meta-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.workflow-meta-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--ailab-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.workflow-meta-value {
  font-size: 13px;
  color: var(--ailab-text-primary);
}

.workflow-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-indicator.active {
  background: #3ba55c;
  box-shadow: 0 0 8px rgba(59, 165, 92, 0.5);
}

.status-indicator.inactive {
  background: var(--ailab-text-muted);
}

.status-text {
  font-size: 13px;
  color: var(--ailab-text-primary);
}

.workflow-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 0;
  border-top: 1px solid var(--ailab-divider);
  border-bottom: 1px solid var(--ailab-divider);
}

.workflow-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  background: var(--ailab-bg-subtle);
  color: var(--ailab-text-primary);
  border: 1px solid var(--ailab-border);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.workflow-action-btn:hover {
  background: var(--ailab-bg-surface);
  border-color: var(--ailab-border-strong);
}

.workflow-action-btn.danger {
  color: var(--ailab-danger);
}

.workflow-action-btn.danger:hover {
  background: rgba(243, 139, 168, 0.1);
  border-color: rgba(243, 139, 168, 0.3);
}

.workflow-card-footer {
  margin-top: 16px;
}

.view-details {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--ailab-accent);
  transition: all 0.2s ease;
}

.workflow-card:hover .view-details {
  color: var(--ailab-accent-hover);
  gap: 8px;
}

/* Workflow List (List View) */
.workflows-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.workflow-list-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: var(--ailab-bg-surface);
  border: 1px solid var(--ailab-border);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.workflow-list-item:hover {
  border-color: var(--ailab-border-strong);
  background: var(--ailab-bg-surface-hover);
}

.workflow-list-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(91, 95, 199, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ailab-accent-soft);
  flex-shrink: 0;
}

.workflow-list-content {
  flex: 1;
  min-width: 0;
}

.workflow-list-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--ailab-text-primary);
  margin-bottom: 2px;
}

.workflow-list-description {
  font-size: 13px;
  color: var(--ailab-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.workflow-list-meta {
  display: flex;
  align-items: center;
  gap: 16px;
}

.workflow-status-inline {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-text-sm {
  font-size: 12px;
  color: var(--ailab-text-secondary);
}

.workflow-list-date {
  font-size: 12px;
  color: var(--ailab-text-muted);
}

.workflow-list-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid var(--ailab-border);
  border-radius: 6px;
  color: var(--ailab-danger);
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: 0;
}

.workflow-list-item:hover .workflow-list-delete {
  opacity: 1;
}

.workflow-list-delete:hover {
  background: rgba(243, 139, 168, 0.1);
  border-color: rgba(243, 139, 168, 0.3);
}

.workflow-list-arrow {
  color: var(--ailab-text-muted);
  transition: all 0.2s ease;
}

.workflow-list-item:hover .workflow-list-arrow {
  color: var(--ailab-text-secondary);
  transform: translateX(4px);
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
  padding: 16px;
}

.modal-content {
  width: 100%;
  max-width: 500px;
  background: var(--ailab-bg-surface);
  border: 1px solid var(--ailab-border);
  border-radius: 12px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--ailab-divider);
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: var(--ailab-text-primary);
}

.modal-close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--ailab-border);
  border-radius: 6px;
  color: var(--ailab-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: var(--ailab-overlay-soft);
  border-color: var(--ailab-border-strong);
}

.modal-body {
  padding: 24px;
}

.inboxes-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.inbox-row {
  padding: 14px 16px;
  border: 1px solid var(--ailab-border);
  border-radius: 8px;
  background: var(--ailab-bg-subtle);
}

.modal-select {
  width: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid var(--ailab-border);
  background: var(--ailab-bg-subtle);
  color: var(--ailab-text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-select:focus {
  outline: none;
  border-color: var(--ailab-accent);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}
</style>
